import { cn } from '@/lib/utils';
import type { SubagentTimelineItem } from '@/hooks/useChat';

interface PaperWorkingBubbleProps {
  show: boolean;
  status: string;
  subagents?: SubagentTimelineItem[];
}

interface PaperSubagentBranchProps {
  subagents: SubagentTimelineItem[];
  parentId: number | null;
  depth?: number;
}

const DEFAULT_LABEL = 'Working';

const statusLabel = (status?: string) =>
  status && status.trim() ? status : DEFAULT_LABEL;

// Mirrors WorkingBubble's grouping: top-level renders any subagent whose
// parent is missing from the active set; deeper levels render exact matches.
const childrenFor = (
  subagents: SubagentTimelineItem[],
  parentId: number | null,
) => {
  const activeIds = new Set(subagents.map((agent) => agent.agentId));
  return subagents.filter((agent) =>
    parentId == null
      ? agent.parentId == null ||
        agent.parentId === 0 ||
        !activeIds.has(agent.parentId)
      : agent.parentId === parentId,
  );
};

const PaperSubagentBranch = (props: PaperSubagentBranchProps) => {
  const { subagents, parentId, depth = 0 } = props;
  const children = childrenFor(subagents, parentId);
  if (children.length === 0) return null;

  return (
    <div
      className={cn(
        'subagent-working-branch-paper mt-[8px] ml-[18px] pl-[12px] flex flex-col gap-[8px]',
        'border-l border-solid',
        depth > 0 && 'ml-[14px]',
      )}
    >
      {children.map((agent) => (
        <div key={agent.agentId}>
          <div className="relative flex justify-start">
            <span className="subagent-working-connector-paper absolute -left-[12px] top-1/2 h-px w-[12px]" />
            <div
              className={cn(
                'working-bubble-paper',
                'inline-flex items-center py-[6px] px-[12px]',
                'rounded-full',
                'text-[10px] max-w-full',
              )}
            >
              <span className="working-shimmer-paper truncate">
                {statusLabel(agent.workingStatus)}…
              </span>
            </div>
          </div>
          <PaperSubagentBranch
            subagents={subagents}
            parentId={agent.agentId}
            depth={depth + 1}
          />
        </div>
      ))}
    </div>
  );
};

export const PaperWorkingBubble = (props: PaperWorkingBubbleProps) => {
  if (!props.show) return null;
  const subagents = props.subagents ?? [];
  const label = statusLabel(props.status);
  return (
    <div className="flex flex-col items-start max-w-full">
      <div
        className={cn(
          'working-bubble-paper',
          'inline-flex items-center py-[8px] px-[14px]',
          'rounded-full',
          'text-[11px]',
        )}
      >
        <span className="working-shimmer-paper">{label}…</span>
      </div>
      <PaperSubagentBranch subagents={subagents} parentId={null} />
    </div>
  );
};
